// pdfTicketsContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { useApiData } from '../contexts/apiService';
import { FIREBASE_STORAGE } from '../firebase/firebase';
import { getDownloadURL, ref, listAll } from 'firebase/storage';

interface TicketsContextType {
    user: User | null;
    availableTickets: string[];
    unacceptedPdfs: TicketPdf[];
}

interface TicketPdf {
    id: string;
    name: string;
    path: string;
    downloadURL: string;
}

export const TicketsContext = createContext<TicketsContextType | undefined>(undefined);

export const TicketsProvider: React.FC<{children: React.ReactNode; user: User | null;}> = ({ children, user }) => {
    const { isLoading: isUserDetailsLoading } = useApiData();
    const [availableTickets, setAvailableTickets] = useState<string[]>([]);
    const [unacceptedPdfs, setUnacceptedPdfs] = useState<TicketPdf[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchRecordFiles = async () => {
        try {
            // Reference to the tickets folder
            const ticketsRef = ref(FIREBASE_STORAGE, 'tickets');
            // List all subfolders (each representing a producer)
            const listResult = await listAll(ticketsRef);
      
            // Fetch record.txt from each producer folder
            const ticketPromises = listResult.prefixes.map(async (folderRef) => {
                const recordRef = ref(folderRef, 'record.txt');
                    try {
                        const url = await getDownloadURL(recordRef);
                        const response = await fetch(url);
                        const text = await response.text();
                        return text.split('\n').filter(line => line.trim() !== '');
                    } catch (err) {
                        console.error(`Error fetching record for ${folderRef.fullPath}`, err);
                        return [];
                    }
            });
        
            // Wait for all fetches to complete and flatten the results
            const ticketsArrays = await Promise.all(ticketPromises);
            const allTickets = ticketsArrays.flat();
            setAvailableTickets(allTickets);
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user && !isUserDetailsLoading) {
            fetchRecordFiles();
        }
    }, [user, isUserDetailsLoading]);
            
    useEffect(() => {
        if (user && !isUserDetailsLoading) {
            const fetchUnaccepted = async () => {
                try {
                    // Reference to the tickets folder
                    const ticketsRef = ref(FIREBASE_STORAGE, 'unaccepted_tickets');
                    const listResult = await listAll(ticketsRef);

                    // Fetch record.txt from each producer folder
                    const pdfPromises = listResult.items.map(async (fileRef) => {
                        try {
                            // Get the download URL for the file
                            const downloadURL = await getDownloadURL(fileRef);
                          
                            // Return simple ticket object with only essential info
                            return {
                                id: fileRef.name,
                                name: fileRef.name,
                                path: fileRef.fullPath,
                                downloadURL: downloadURL,
                            };
                        } catch (err) {
                            console.error(`Error processing file ${fileRef.name}`, err);
                            return null;
                        }
                    });
                
                    // Wait for all promises to resolve and filter out null values
                    const pdfs = (await Promise.all(pdfPromises)).filter((pdf): pdf is TicketPdf => pdf !== null);
                    pdfs.sort((a, b) => a.name.localeCompare(b.name));
                    setUnacceptedPdfs(pdfs)

                } catch (err) {
                    if (err instanceof Error) {
                        setError(err.message);
                    } else {
                        setError('An unexpected error occurred');
                    }
                    console.error('Error fetching tickets:', err);
                } finally {
                    setLoading(false);
                }
            };
            fetchUnaccepted();
        }
    }, [user, isUserDetailsLoading]);

    return (
        <TicketsContext.Provider value={{ user, availableTickets, unacceptedPdfs }}>
            {children}
        </TicketsContext.Provider>
    );
};
    
export const useTicketsContext = (): TicketsContextType => {
    const context = useContext(TicketsContext);
    if (!context) {
        throw new Error('useTicketsContext must be used within a TicketsProvider');
    }
    return context;
};
    
export default TicketsContext;